import { useLocation } from 'react-router-dom';
import { RequireAssessments } from '../components/DependencyGate';
import {
  Datagrid,
  List,
  Create,
  Edit,
  SimpleForm,
  DateField,
  DateTimeInput,
  ReferenceInput,
  ReferenceArrayInput,
  SelectInput,
  SelectArrayInput,
  SimpleFormIterator,
  ArrayInput,
  TextField,
  ReferenceField,
  Show,
  SimpleShowLayout,
  ArrayField,
  ReferenceArrayField,
  SingleFieldList,
  required,
  usePermissions,
  useRecordContext,
} from 'react-admin';
import { Button, Box } from '@mui/material';
import AddIcon from '@mui/icons-material/AddCircleOutline';
import {
  AssessmentField,
  AssessmentSelect,
  ClassSelect,
  FullNameField,
  getFullName,
} from '../components/selects';
import { useAssessmentIds } from '../utils/useCourseIds';
import { validateFlights } from './assessments-helpers';
import { hasRole, type Permissions } from '../types';

const InstanceList = () => {
  const assessment_ids = useAssessmentIds();
  const { permissions } = usePermissions<Permissions>();

  return (
    <RequireAssessments>
    <List
      exporter={false}
      filters={[
        <AssessmentSelect key="assessment" alwaysOn />,
        <ClassSelect key="class" alwaysOn />,
      ]}
      filter={assessment_ids ? { assessment_id: assessment_ids } : undefined}
      sort={{ field: 'date', order: 'ASC' }}
      perPage={25}
    >
      <Datagrid
        rowClick={hasRole(permissions, 'tenant_admin') ? 'edit' : 'show'}
      >
        <DateField
          showTime
          options={{ hour12: false }}
          source="date"
          label="Date of Instance"
        />
        <ReferenceField
          label="Assessment"
          source="assessment_id"
          reference="assessments"
        >
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField
          label="Class"
          source="student_group_id"
          reference="student_groups"
        >
          <TextField source="name" />
        </ReferenceField>
      </Datagrid>
    </List>
    </RequireAssessments>
  );
};

const InstanceShow = () => (
  <Show>
    <SimpleShowLayout>
      <AssessmentField />
      <DateField
        showTime
        options={{ hour12: false }}
        source="date"
        label="Scheduled Date & Time"
      />
      <DateField
        showTime
        options={{ hour12: false }}
        label="Date Performed"
        source="started_at"
      />
      <ReferenceField
        label="Started By"
        source="started_by"
        reference="users"
      >
        <FullNameField />
      </ReferenceField>
      <ArrayField label="Flights" source="lanes">
        <Datagrid bulkActionButtons={false}>
          <ReferenceArrayField
            label="Instructors"
            source="instructors"
            reference="users"
            perPage={100}
          >
            <SingleFieldList>
              <FullNameField />
            </SingleFieldList>
          </ReferenceArrayField>
          <ArrayField label="Fire Teams" source="teams">
            <SingleFieldList>
              <FireTeamField />
            </SingleFieldList>
          </ArrayField>
        </Datagrid>
      </ArrayField>
    </SimpleShowLayout>
  </Show>
);

const FireTeamField = () => {
  const record = useRecordContext();
  if (!record?.participants && !record?.label) return null;
  const studentIds = record?.participants || record?.label || [];
  return (
    <ReferenceArrayField
      label="Participants"
      reference="students"
      record={{ ...record, student_ids: studentIds }}
      source="student_ids"
    >
      <SingleFieldList>
        <FullNameField />
      </SingleFieldList>
    </ReferenceArrayField>
  );
};

const InstanceCreate = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const prefillAssessmentId = params.get('assessment_id') ?? undefined;

  return (
    <Create>
      <InstanceForm defaultAssessmentId={prefillAssessmentId} />
    </Create>
  );
};

const InstanceEdit = () => (
  <Edit mutationMode="pessimistic">
    <InstanceForm />
  </Edit>
);


const LabelButton = ({ label = 'Add' }: { label?: string }) => (
  <Button size="small" startIcon={<AddIcon />}>
    {label}
  </Button>
);

const InstanceForm = ({ defaultAssessmentId }: { defaultAssessmentId?: string }) => (
  <SimpleForm defaultValues={defaultAssessmentId ? { assessment_id: defaultAssessmentId } : {}}>
    <Box display="flex" gap={2} width="100%">
      <ReferenceInput
        label="Assessment"
        source="assessment_id"
        reference="assessments"
        perPage={100}
      >
        <SelectInput optionText="name" sx={{ minWidth: 300 }} />
      </ReferenceInput>
      <DateTimeInput source="date" label="Scheduled Date & Time" />
    </Box>
    <Box display="flex" gap={2} width="100%">
      <DateTimeInput label="Date Performed" source="started_at" />
      <ReferenceInput
        perPage={100}
        label="Started By"
        source="started_by"
        reference="users"
      >
        <SelectInput optionText={getFullName} sx={{ minWidth: 300 }} />
      </ReferenceInput>
    </Box>

    <ArrayInput validate={validateFlights} label="Flights" source="lanes">
      <SimpleFormIterator
        addButton={<LabelButton label="Add Flight" />}
      >
        <ReferenceArrayInput
          validate={[required()]}
          perPage={100}
          label="Instructors"
          source="instructors"
          reference="users"
        >
          <SelectArrayInput optionText={getFullName} />
        </ReferenceArrayInput>

        <ArrayInput validate={[required()]} label="Fire Teams" source="teams">
          <SimpleFormIterator
            addButton={<LabelButton label="Add Fire Team" />}
          >
            <ReferenceArrayInput
              validate={[required()]}
              perPage={100}
              label="Participants"
              source="participants"
              reference="students"
            >
              <SelectArrayInput optionText={getFullName} />
            </ReferenceArrayInput>
          </SimpleFormIterator>
        </ArrayInput>
      </SimpleFormIterator>
    </ArrayInput>
  </SimpleForm>
);

export const instances = {
  list: InstanceList,
  show: InstanceShow,
  create: InstanceCreate,
  edit: InstanceEdit,
};
